import { useApiResource } from '~/composables/api/useApiResource'
import { buildApiParams } from '~/utils/api/buildApiParams'

export const usePromotionApi = () => {
  const { loading, error, list, getById, create, update, customEndpoint } = useApiResource('promotions', {
    deserialize: true,
    methods: {
      list: true,
      getById: true,
      create: true,
      update: true,
      delete: false
    }
  })

  const getPromotionBudget = customEndpoint((id, params = {}) => ({
    url: `/promotions/${id}/budgets`,
    method: 'GET',
    params: buildApiParams(params)
  }))

  const getItemQuotes = customEndpoint((promotionId, itemId, params = {}) => ({
    url: `/promotions/${promotionId}/budgets/${itemId}/quotes`,
    method: 'GET',
    params: buildApiParams({ include: ['company'], ...params }),
    deserialize: false
  }))

  const getPromotionQuotes = customEndpoint((promotionId) => ({
    url: `/promotions/${promotionId}/quotes`,
    method: 'GET',
    deserialize: false
  }))

  const getBudgetItems = customEndpoint((promotionId, params = {}) => ({
    url: `/promotions/${promotionId}/budgets/items`,
    method: 'GET',
    params: buildApiParams(params),
    deserialize: true
  }))

  const createBudgetItem = customEndpoint((promotionId, data) => ({
    url: `/promotions/${promotionId}/budgets/items`,
    method: 'POST',
    data,
    deserialize: false
  }))

  const getNextBudgetCode = customEndpoint((promotionId, data) => ({
    url: `/promotions/${promotionId}/budgets/next-code`,
    method: 'POST',
    data,
    deserialize: false
  }))

  const updateBudgetItem = customEndpoint((promotionId, itemId, data) => ({
    url: `/promotions/${promotionId}/budgets/items/${itemId}`,
    method: 'PATCH',
    data,
    deserialize: false
  }))

  const deleteBudgetItem = customEndpoint((promotionId, itemId) => ({
    url: `/promotions/${promotionId}/budgets/items/${itemId}`,
    method: 'DELETE',
    deserialize: true
  }))

  const forceEditBudgetItem = customEndpoint((promotionId, itemId) => ({
    url: `/promotions/${promotionId}/budgets/items/${itemId}/states`,
    method: 'PATCH',
    deserialize: true
  }))

  const getChaptersProgress = customEndpoint((promotionId) => ({
    url: `/promotions/${promotionId}/budgets/items/chapters-progress`,
    method: 'GET',
    deserialize: true
  }))

  const getPromotionMetrics = customEndpoint((promotionId) => ({
    url: `/promotions/${promotionId}/metrics`,
    method: 'GET',
    deserialize: true
  }))

  const getCompanyPromotionMetrics = customEndpoint((promotionId) => ({
    url: `/promotions/${promotionId}/company-metrics`,
    method: 'GET',
    deserialize: true
  }))

  const createBudget = customEndpoint((promotionId, options = {}) => ({
    url: `/promotions/${promotionId}/budgets`,
    method: 'POST',
    data: {},
    customHeaders: options.confirmReplace ? { 'X-Confirm-Budget-Replace': 'true' } : {},
    deserialize: true
  }))

  const importBudget = customEndpoint((promotionId, fileId, options = {}) => ({
    url: `/promotions/${promotionId}/budgets/imports`,
    method: 'POST',
    data: { fileId },
    customHeaders: options.confirmReplace ? { 'X-Confirm-Budget-Replace': 'true' } : {},
    deserialize: true
  }))

  const getPromotionCompanies = customEndpoint((promotionId, params = {}) => ({
    url: `/promotions/${promotionId}/companies`,
    method: 'GET',
    params: buildApiParams(params)
  }))

  const deletePromotion = customEndpoint((promotionId, confirmationCode) => ({
    url: `/promotions/${promotionId}`,
    method: 'DELETE',
    data: { confirmationCode },
    deserialize: false
  }))

  const listBudgetItemInvitations = customEndpoint((itemId) => ({
    url: '/invitations',
    method: 'GET',
    params: { 'filter[budgetItemId]': itemId, 'page[size]': 5000 },
    deserialize: false
  }))

  const revokeBudgetItemInvitation = customEndpoint((invitationId) => ({
    url: `/invitations/${invitationId}/revoke`,
    method: 'POST',
    deserialize: false
  }))

  const listMyBudgetItemInvitations = customEndpoint((promotionId) => ({
    url: '/me/invitations',
    method: 'GET',
    params: { direction: 'received', 'filter[type]': 'budget', 'filter[promotionId]': promotionId, 'page[size]': 5000 },
    deserialize: false
  }))

  const listMyPromotionInvitations = customEndpoint((promotionId) => ({
    url: '/me/invitations',
    method: 'GET',
    params: { direction: 'received', 'filter[type]': 'promotion', 'filter[promotionId]': promotionId, 'page[size]': 100 },
    deserialize: false
  }))

  const acceptBudgetItemInvitation = customEndpoint((invitationId) => ({
    url: `/invitations/${invitationId}/accept`,
    method: 'POST',
    deserialize: false
  }))

  const rejectBudgetItemInvitation = customEndpoint((invitationId) => ({
    url: `/invitations/${invitationId}/reject`,
    method: 'POST',
    deserialize: false
  }))

  const listMyQuotes = customEndpoint((promotionId) => ({
    url: `/promotions/${promotionId}/budgets/my-quotes`,
    method: 'GET',
    deserialize: false
  }))

  /** Ventanas planificadas de los subcapítulos ofertables (#135) */
  const listPlannedWindows = customEndpoint((promotionId) => ({
    url: `/promotions/${promotionId}/planned-windows`,
    method: 'GET',
    deserialize: false
  }))

  const revokeCompanyAccess = customEndpoint((promotionId, companyId) => ({
    url: `/promotions/${promotionId}/companies/${companyId}/access`,
    method: 'DELETE',
    deserialize: false
  }))

  const revokeCompanyItemAccess = customEndpoint((promotionId, companyId, itemId) => ({
    url: `/promotions/${promotionId}/companies/${companyId}/items/${itemId}/access`,
    method: 'DELETE',
    deserialize: false
  }))

  const createQuote = customEndpoint((promotionId, itemId, data) => ({
    url: `/promotions/${promotionId}/budgets/${itemId}/quotes`,
    method: 'POST',
    data,
    deserialize: false
  }))

  const updateQuote = customEndpoint((promotionId, itemId, quoteId, data) => ({
    url: `/promotions/${promotionId}/budgets/${itemId}/quotes/${quoteId}`,
    method: 'PATCH',
    data,
    deserialize: false
  }))

  const acceptQuote = customEndpoint((promotionId, itemId, quoteId) => ({
    url: `/promotions/${promotionId}/budgets/${itemId}/quotes/${quoteId}/accept`,
    method: 'PATCH',
    deserialize: false
  }))

  const rejectQuote = customEndpoint((promotionId, itemId, quoteId) => ({
    url: `/promotions/${promotionId}/budgets/${itemId}/quotes/${quoteId}/reject`,
    method: 'PATCH',
    deserialize: false
  }))

  const sendQuote = customEndpoint((promotionId, itemId, quoteId) => ({
    url: `/promotions/${promotionId}/budgets/${itemId}/quotes/${quoteId}/send`,
    method: 'POST',
    deserialize: false
  }))

  const discardQuote = customEndpoint((promotionId, itemId, quoteId) => ({
    url: `/promotions/${promotionId}/budgets/${itemId}/quotes/${quoteId}/discard`,
    method: 'POST',
    deserialize: false
  }))

  const confirmQuote = customEndpoint((promotionId, itemId, quoteId) => ({
    url: `/promotions/${promotionId}/budgets/${itemId}/quotes/${quoteId}/confirm`,
    method: 'PATCH',
    deserialize: false
  }))

  const markQuoteViewed = customEndpoint((promotionId, itemId, quoteId) => ({
    url: `/promotions/${promotionId}/budgets/${itemId}/quotes/${quoteId}/viewed`,
    method: 'PATCH',
    deserialize: false
  }))

  const openNegotiation = customEndpoint((promotionId, itemId, quoteId) => ({
    url: `/promotions/${promotionId}/budgets/${itemId}/quotes/${quoteId}/open-negotiation`,
    method: 'PATCH',
    deserialize: false
  }))

  const createBudgetParticipations = customEndpoint((promotionId, budgetItemIds) => ({
    url: `/promotions/${promotionId}/budgets/participations`,
    method: 'POST',
    data: { budgetItemIds },
    deserialize: false
  }))

  return {
    loading,
    error,
    getPromotions: list,
    getPromotionById: getById,
    createPromotion: create,
    updatePromotion: update,
    getPromotionBudget,
    getItemQuotes,
    getPromotionQuotes,
    getBudgetItems,
    createBudgetItem,
    getNextBudgetCode,
    updateBudgetItem,
    deleteBudgetItem,
    forceEditBudgetItem,
    getChaptersProgress,
    getPromotionMetrics,
    getCompanyPromotionMetrics,
    createBudget,
    importBudget,
    getPromotionCompanies,
    deletePromotion,
    listBudgetItemInvitations,
    revokeBudgetItemInvitation,
    listMyBudgetItemInvitations,
    listMyPromotionInvitations,
    acceptBudgetItemInvitation,
    rejectBudgetItemInvitation,
    listMyQuotes,
    listPlannedWindows,
    revokeCompanyAccess,
    revokeCompanyItemAccess,
    createQuote,
    updateQuote,
    acceptQuote,
    rejectQuote,
    sendQuote,
    discardQuote,
    confirmQuote,
    markQuoteViewed,
    openNegotiation,
    createBudgetParticipations
  }
}
